<template>
  <div class="google">
    <SingleLevelBreadcrumb
      label="Google"
      class="google__breadcrumb"
    />
    <div class="google__section--no-padding">
      <GoogleStage
        :data="STAGE_DATA"
        @go-to-section="goToSection"
      />
    </div>
    <div class="google__section">
      <VideoPlayer
        ref="videoPlayer"
        :src="VIDEO_DATA.src"
        :poster="VIDEO_DATA.poster"
        class="google-intro__video"
        muted
      />
    </div>
    <div class="google__section">
      <GoogleInfoTiles
        :tiles-data="TILES_DATA[0]"
        :show-link="true"
        :title="TILES_DATA[0].title"
        :subtitle="TILES_DATA[0].subtitle"
        @go-to-section="goToSection"
      />
    </div>
    <div
      v-if="firstTeaser && firstTeaser.offer"
      class="google__section"
    >
      <div
        ref="pixelBudsElement"
        class="google__section__anchor"
      ></div>
      <ProductOfferTileXlContainer
        :ambient-image="firstTeaser.ambientImage"
        :description="firstTeaser.copy"
        :offer="firstTeaser.offer"
        :product-image="firstTeaser.productImage"
        :title="firstTeaser.title"
        class="google__teaser-xl google__mb"
        show-local-image
        show-local-title
        lazy
      />
    </div>
    <div
      v-if="tableTeaser.length && offers[1]?.offer"
      class="google__section"
    >
      <div
        ref="specsTable"
        class="google__section__anchor"
      ></div>
      <GoogleTable
        :header="tableTeaser[0].title"
        :table-data="SPECS_DATA"
        :offers="tableTeaser"
        :offer="offers[1].offer"
      />
    </div>
    <div
      v-if="secondTeaser && secondTeaser.offer"
      class="google__section"
    >
      <div
        ref="pixelStandElement"
        class="google__section__anchor"
      ></div>

      <div class="google__section__header">
        <Heading
          :text="secondTeaser.headerTitle"
          class="google-stage__grid__item"
          font-weight="ultra"
          size="lg"
          tag="h2"
          text-uppercase
          align="center"
          spaces-top
          spaces-bottom
        />
      </div>
      <ProductOfferTileXlContainer
        :ambient-image="secondTeaser.ambientImage"
        :copy="secondTeaser.copy"
        :offer="secondTeaser.offer"
        :product-image="secondTeaser.productImage"
        :title="secondTeaser.title"
        heading-tag="h3"
        class="google__teaser-xl google__mb"
        show-local-image
        show-local-title
        alignment="right"
        lazy
      />
    </div>
    <div class="google__section">
      <GoogleInfoTiles :tiles-data="TILES_DATA[1]" />
    </div>
    <div
      v-if="thirdTeaser.length"
      class="google__section google__section--no-padding"
    >
      <div
        ref="pixelCasesElement"
        class="google__section__anchor"
      ></div>
      <div class="google__section__header">
        <Heading
          :text="thirdTeaser[0].mainTitle"
          class="google-stage__grid__item"
          font-weight="ultra"
          size="lg"
          tag="h2"
          text-uppercase
          align="center"
          spaces-top
        />
        <Heading
          :text="thirdTeaser[0].subtitle"
          class="google-stage__grid__item"
          font-weight="regular"
          size="sm"
          tag="h3"
          align="center"
          spaces-top
          spaces-bottom
        />
      </div>
      <ProductOfferTileSMContainer
        :items="thirdTeaser"
        show-available-variant
        class="google__mb"
        tile-type="medium"
        heading-tag="h4"
      />
    </div>
    <div class="google__section">
      <GoogleInfoTiles :tiles-data="TILES_DATA[2]" />
    </div>
    <div class="google__section">
      <GoogleTeasers :teaser-items="BANNER_DATA" />
    </div>
    <div class="google__section">
      <GoogleFaq :faqs="FAQS_DATA" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useLazyAsyncData, useHead } from '#imports';
import { ref, computed, nextTick } from 'vue';
import { Heading, VideoPlayer } from '@i22/td-longtail-vue-components';
import SingleLevelBreadcrumb from '~/components/single-level-breadcrumb.vue';
import ProductOfferTileXlContainer from '~/components/product-offer-tile-xl-container.vue';
import ProductOfferTileSMContainer from '~/components/product-offer-tile-s-m-container.vue';
import {
  getOffersByGroup,
  loadOffersForPromotions,
  type PromotionOffer,
} from '~/lib/offers-by-group';
import GoogleStage from '~/components/google/google-stage.vue';
import GoogleInfoTiles from '~/components/google/google-info-tiles.vue';
import GoogleTeasers from '~/components/google/google-teasers.vue';
import GoogleTable from '~/components/google/google-table.vue';
import GoogleFaq from '~/components/google/google-faq.vue';
import { Track } from '~/helpers/page-tracking';
import { getHeader } from '~/helpers/page-header-compositon';
import {
  PRODUCT_DATA,
  SPECS_DATA,
  STAGE_DATA,
  TILES_DATA,
  BANNER_DATA,
  VIDEO_DATA,
  FAQS_DATA,
} from './product-data';

Track({ page_type: 'theme.landing' });

getHeader({
  title: ref('Google Pixel Buds entdecken'),
  description: ref(
    'Die Premium In-Ear-Kopfhörer mit aktivem Noise Cancelling für vollen Klang. Jetzt mehr über die Google Pixel Buds erfahren & passendes Zubehör entdecken!'
  ),
});

const { data: offers, error: errorData } = useLazyAsyncData(
  'GoogleWorld',
  (): Promise<PromotionOffer[]> => loadOffersForPromotions(PRODUCT_DATA)
);

const pixelBudsElement = ref(null);
const pixelStandElement = ref(null);
const pixelCaseElement = ref(null);

const firstTeaser = computed(() => getOffersByGroup(offers.value, 'first')[0]);
const secondTeaser = computed(() => getOffersByGroup(offers.value, 'second')[0]);
const thirdTeaser = computed(() => getOffersByGroup(offers.value, 'third'));
const tableTeaser = computed(() => getOffersByGroup(offers.value, 'table'));

const getElement = (refString: string) => {
  if (refString === 'pixelBuds') {
    return pixelBudsElement.value;
  }
  if (refString === 'pixelStand') {
    return pixelStandElement.value;
  }
  if (refString === 'pixelCase') {
    return pixelCaseElement.value;
  }
  return undefined;
};

const goToSection = (refString: string) => {
  nextTick(() => {
    const element = getElement(refString);
    if (!element) return;
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    element.focus();
  });
};

// json-ld
useHead(() => ({
  script: {
    type: 'application/ld+json',
    innerHTML: JSON.stringify(
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS_DATA.map((item) => ({
          '@type': 'Question',
          name: item.title,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.content,
          },
        })),
      },
      null,
      ''
    ),
  },
}));
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.google {
  &__section {
    position: relative;
    margin-bottom: 6rem;

    &--no-padding {
      margin-bottom: 0;
    }

    &__anchor {
      position: absolute;
      left: 0;
      top: -100px;
    }

    &__header {
      margin: 2rem 0;
    }
  }

  &__teaser {
    &-xl {
      :deep(.product-offer-detail-link__link) {
        img {
          width: 100%;
          aspect-ratio: 16 / 11;
        }
      }

      :deep(.product-offer-tile-xl__ambient-image) {
        img {
          width: 100%;
          aspect-ratio: 20 / 11;
        }
      }
    }
  }
}

:deep(.no-data) {
  .prices__main,
  .product-offer-color-select,
  .product-offer-tile-xl__content-link {
    display: none;
  }

  .product-offer-tile-xl__content-middle {
    justify-content: flex-start;
  }
}
</style>
